import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState, type JSX, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  ArchiveRestore,
  FileText,
  History,
  KeyRound,
  Link as LinkIcon,
  Pencil,
  UserCheck,
  UserPlus,
  UserX,
  Users,
} from 'lucide-react';
import { apiRequest, ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { useConfirm } from '../components/ui/Confirm';
import { formatDate, timeAgo } from '../lib/format';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { GatedSection } from '../components/shared/GatedSection';
import { DetailHeader } from '../components/shared/DetailHeader';
import { KpiStrip, type KpiItem } from '../components/shared/KpiStrip';
import { RelationPanel } from '../components/shared/RelationPanel';
import { ActivityPanel } from '../components/shared/ActivityPanel';
import { AddRelationModal } from '../components/shared/AddRelationModal';
import { EntityPicker, type PickedEntity } from '../components/shared/EntityPicker';
import { TextField } from '../components/ui/Field';
import { StatusBadge } from '../components/people/StatusBadge';
import { OverflowMenu } from '../components/people/OverflowMenu';
import { PortalAccessCard } from '../components/system/PortalAccessCard';
import {
  StudentDrawer,
  type StudentDrawerMode,
} from '../components/people/StudentDrawer';
import type { StudentRecord } from '../components/people/types';

export const Route = createFileRoute('/_authed/people/students/$studentId')({
  component: StudentDetailPage,
});

// ---------------------------------------------------------------------------
// Wire shapes
//
// The backend agent is extending GET /api/students/:id (per plan §4.1) to
// embed household + siblings; until that ships we render defensively when
// any of those keys are missing. The existing payload already carries
// `enrollments[]` (students.ts:172-187).
// ---------------------------------------------------------------------------

interface StudentEnrollmentRow {
  batchId: string;
  batchName: string;
  courseId: string;
  courseName: string;
  status: 'active' | 'paused' | 'completed' | 'withdrawn';
  startDate: string;
  endDate?: string | null;
}

interface SiblingSummary {
  id: string;
  name: string;
  admissionNumber: string;
}

interface HouseholdSummary {
  id: string;
  primaryContactName: string;
  primaryPhone: string;
  primaryEmail: string;
  relation?: string | null;
  isPrimary?: boolean;
}

interface StudentDetail extends StudentRecord {
  enrollments?: StudentEnrollmentRow[];
  household?: HouseholdSummary | null;
  siblings?: SiblingSummary[];
}

type Tab = 'overview' | 'enrollments' | 'household' | 'portal' | 'activity';

const TABS: ReadonlyArray<{ value: Tab; label: string; icon: typeof Users }> = [
  { value: 'overview', label: 'Overview', icon: FileText },
  { value: 'enrollments', label: 'Enrollments', icon: Users },
  { value: 'household', label: 'Family', icon: LinkIcon },
  { value: 'portal', label: 'Portal access', icon: KeyRound },
  { value: 'activity', label: 'Activity', icon: History },
];

function StudentDetailPage(): JSX.Element {
  const { studentId } = Route.useParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const callerId = useAuthStore((s) => s.user?.id ?? null);
  const canManage =
    role === 'super_admin' || role === 'academic_admin' || role === 'nalim';
  const isSuperAdmin = role === 'super_admin';

  const [tab, setTab] = useState<Tab>('overview');
  const [editDrawer, setEditDrawer] = useState<StudentDrawerMode | null>(null);
  const [overflowOpen, setOverflowOpen] = useState(false);

  const studentQuery = useQuery({
    queryKey: ['student', 'detail', studentId] as const,
    queryFn: ({ signal }) =>
      apiRequest<StudentDetail>(
        `/api/students/${encodeURIComponent(studentId)}`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const student = studentQuery.data;

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['student', 'detail', studentId] });
    void queryClient.invalidateQueries({ queryKey: ['students'] });
  };

  const archiveMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>(`/api/students/${encodeURIComponent(studentId)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      toast('Student archived.', { variant: 'success' });
      invalidate();
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not archive student.', {
        variant: 'error',
      });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>(
        `/api/students/${encodeURIComponent(studentId)}/restore`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      toast('Student restored.', { variant: 'success' });
      invalidate();
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not restore student.', {
        variant: 'error',
      });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>(
        `/api/students/${encodeURIComponent(studentId)}/deactivate`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      toast(`${student?.name ?? 'Student'} deactivated.`, { variant: 'success' });
      invalidate();
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not deactivate student.', {
        variant: 'error',
      });
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>(
        `/api/students/${encodeURIComponent(studentId)}/reactivate`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      toast(`${student?.name ?? 'Student'} reactivated.`, { variant: 'success' });
      invalidate();
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not reactivate student.', {
        variant: 'error',
      });
    },
  });

  const handleArchive = async (): Promise<void> => {
    if (!student) return;
    const ok = await confirm({
      title: 'Archive student?',
      description: `${student.name} will be moved to archived status and lose portal access.`,
      confirmLabel: 'Archive',
      variant: 'danger',
    });
    if (!ok) return;
    setOverflowOpen(false);
    archiveMutation.mutate();
  };

  const handleRestore = async (): Promise<void> => {
    if (!student) return;
    const ok = await confirm({
      title: 'Restore student?',
      description: `${student.name} will be returned to active status.`,
      confirmLabel: 'Restore',
    });
    if (!ok) return;
    restoreMutation.mutate();
  };

  const handleDeactivate = async (): Promise<void> => {
    if (!student) return;
    const ok = await confirm({
      title: `Deactivate ${student.name}?`,
      description:
        'Their portal login will be disabled immediately and all active sessions ended. They stay on the active roster — use Archive to file them away instead.',
      confirmLabel: 'Deactivate',
      variant: 'danger',
    });
    if (!ok) return;
    setOverflowOpen(false);
    deactivateMutation.mutate();
  };

  const handleReactivate = async (): Promise<void> => {
    if (!student) return;
    const ok = await confirm({
      title: `Reactivate ${student.name}?`,
      description: `${student.name} will be returned to active status.`,
      confirmLabel: 'Reactivate',
    });
    if (!ok) return;
    reactivateMutation.mutate();
  };

  const kpiItems = useMemo<KpiItem[]>(() => {
    if (!student) return [];
    const enrollments = student.enrollments ?? [];
    const activeCount = enrollments.filter((e) => e.status === 'active').length;
    const siblings = student.siblings ?? [];
    return [
      {
        label: 'Active enrollments',
        value: activeCount,
        onClick: () => setTab('enrollments'),
      },
      {
        label: 'Family',
        value: student.household?.primaryContactName ?? 'Not linked',
        onClick: () => setTab('household'),
      },
      {
        label: 'Siblings',
        value: siblings.length,
        onClick: () => setTab('household'),
      },
      {
        label: 'Portal',
        value: !student.userId
          ? 'Not invited'
          : student.status === 'inactive'
            ? 'Deactivated'
            : student.status === 'archived'
              ? 'Disabled'
              : 'Active',
        onClick: () => setTab('portal'),
      },
      {
        label: 'Joined',
        value: timeAgo(student.createdAt),
      },
    ];
  }, [student]);

  const archived = !!student?.deletedAt || student?.status === 'archived';
  const isSelfLinked = !!student?.userId && student.userId === callerId;
  const canDeactivate =
    isSuperAdmin &&
    !archived &&
    student?.status === 'active' &&
    !!student?.userId &&
    !isSelfLinked;
  const canReactivate =
    isSuperAdmin && !archived && student?.status === 'inactive';

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto w-full">
      <GatedSection
        error={studentQuery.error}
        title="Student"
        onRetry={() => studentQuery.refetch()}
      >
        {studentQuery.isLoading || !student ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <DetailHeader
              title={student.name}
              sublabel={
                <>
                  <code className="rounded bg-[color:var(--color-surface)] px-1.5 py-0.5 font-mono text-[11px] text-[color:var(--color-text-muted)]">
                    {student.admissionNumber}
                  </code>
                  <span aria-hidden="true">·</span>
                  <span className="capitalize">{student.programMode}</span>
                  <span aria-hidden="true">·</span>
                  <span className="capitalize">{student.gender}</span>
                </>
              }
              statusChip={<StatusBadge status={student.status} />}
              backTo="/people/students"
              actions={
                canManage ? (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<Pencil size={14} aria-hidden="true" />}
                      onClick={() => setEditDrawer({ kind: 'edit', id: student.id })}
                    >
                      Edit
                    </Button>
                    {archived ? (
                      <Button
                        size="sm"
                        icon={<ArchiveRestore size={14} aria-hidden="true" />}
                        onClick={() => {
                          void handleRestore();
                        }}
                        loading={restoreMutation.isPending}
                      >
                        Restore
                      </Button>
                    ) : null}
                    {canReactivate ? (
                      <Button
                        size="sm"
                        icon={<UserCheck size={14} aria-hidden="true" />}
                        onClick={() => {
                          void handleReactivate();
                        }}
                        loading={reactivateMutation.isPending}
                      >
                        Reactivate
                      </Button>
                    ) : null}
                    {canDeactivate ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<UserX size={14} aria-hidden="true" />}
                        className="text-danger hover:bg-[color:var(--color-danger)]/10"
                        onClick={() => {
                          void handleDeactivate();
                        }}
                        loading={deactivateMutation.isPending}
                      >
                        Deactivate
                      </Button>
                    ) : null}
                    {!archived ? (
                      <OverflowMenu
                        open={overflowOpen}
                        onOpenChange={setOverflowOpen}
                        pending={archiveMutation.isPending}
                        onArchive={() => {
                          void handleArchive();
                        }}
                      />
                    ) : null}
                  </>
                ) : null
              }
            />

            <KpiStrip items={kpiItems} />

            <div
              role="tablist"
              aria-label="Student sections"
              className="flex flex-wrap gap-1 border-b border-[color:var(--color-border)]"
            >
              {TABS.map(({ value, label, icon: Icon }) => {
                const active = tab === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTab(value)}
                    className={clsx(
                      'inline-flex items-center gap-1.5 rounded-t-md px-3 py-2 text-sm font-medium transition-colors',
                      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
                      active
                        ? 'border-b-2 border-gold-500 text-[color:var(--color-text)]'
                        : 'text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]',
                    )}
                  >
                    <Icon size={14} aria-hidden="true" />
                    {label}
                  </button>
                );
              })}
            </div>

            {tab === 'overview' ? <OverviewTab student={student} /> : null}
            {tab === 'enrollments' ? (
              <EnrollmentsTab
                student={student}
                canManage={canManage}
                onChanged={invalidate}
              />
            ) : null}
            {tab === 'household' ? (
              <HouseholdTab
                student={student}
                canManage={canManage}
                onChanged={invalidate}
              />
            ) : null}
            {tab === 'portal' ? <PortalTab student={student} onChanged={invalidate} /> : null}
            {tab === 'activity' ? (
              <ActivityPanel
                targetType="student"
                targetId={student.id}
                title="Activity"
              />
            ) : null}
          </div>
        )}
      </GatedSection>

      {canManage && student ? (
        <StudentDrawer
          mode={editDrawer}
          onClose={() => setEditDrawer(null)}
          initial={student}
          isLoadingInitial={false}
          loadError={null}
          showArchive={false}
          onPortalChange={invalidate}
          onSaved={() => {
            setEditDrawer(null);
            invalidate();
            toast('Student saved.', { variant: 'success' });
          }}
          onArchive={async () => {
            await handleArchive();
            setEditDrawer(null);
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview tab
// ---------------------------------------------------------------------------

function OverviewTab({ student }: { student: StudentDetail }): JSX.Element {
  return (
    <section className="grid gap-4 lg:grid-cols-2">
      <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
          Identity
        </h3>
        <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
          <DetailRow label="Admission #" value={<code className="font-mono text-xs">{student.admissionNumber}</code>} />
          <DetailRow label="Date of birth" value={formatDate(student.dateOfBirth)} />
          <DetailRow label="Gender" value={<span className="capitalize">{student.gender}</span>} />
          <DetailRow label="Program mode" value={<span className="capitalize">{student.programMode}</span>} />
          <DetailRow label="Status" value={<StatusBadge status={student.status} />} />
          <DetailRow label="Fee due day" value={String(student.feeDueDay)} />
        </dl>
      </div>
      <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
          Guardian & contact
        </h3>
        <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
          <DetailRow label="Guardian" value={student.guardianName} />
          <DetailRow label="Relation" value={student.guardianRelation} />
          <DetailRow label="Phone" value={student.contact} />
          <DetailRow label="Email" value={student.email ?? '—'} />
          <DetailRow
            label="Address"
            value={
              <span className="whitespace-pre-wrap text-[color:var(--color-text)]">
                {student.address}
              </span>
            }
          />
        </dl>
      </div>
      {student.notes ? (
        <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5 lg:col-span-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
            Notes
          </h3>
          <p className="mt-2 whitespace-pre-wrap text-sm text-[color:var(--color-text)]">
            {student.notes}
          </p>
        </div>
      ) : null}
    </section>
  );
}

function DetailRow({ label, value }: { label: string; value: ReactNode }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-[color:var(--color-border)] pb-2 last:border-b-0 last:pb-0">
      <dt className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {label}
      </dt>
      <dd className="text-sm text-[color:var(--color-text)]">{value}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Enrollments tab
// ---------------------------------------------------------------------------

interface EnrollmentsTabProps {
  student: StudentDetail;
  canManage: boolean;
  onChanged: () => void;
}

function EnrollmentsTab({ student, canManage, onChanged }: EnrollmentsTabProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const enrollments = student.enrollments ?? [];

  return (
    <>
      <RelationPanel
        title="Enrollments"
        count={enrollments.length}
        description="Batches this student belongs to."
        action={
          canManage ? (
            <Button
              size="sm"
              icon={<UserPlus size={14} aria-hidden="true" />}
              onClick={() => setOpen(true)}
            >
              Enroll in batch
            </Button>
          ) : null
        }
        isEmpty={enrollments.length === 0}
        empty={{
          copy: 'Not enrolled in any batch yet.',
          cta: canManage ? (
            <Button
              size="sm"
              icon={<UserPlus size={14} aria-hidden="true" />}
              onClick={() => setOpen(true)}
            >
              Enroll in batch
            </Button>
          ) : undefined,
        }}
      >
        <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)]">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--color-surface)] text-left text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
              <tr>
                <th className="px-4 py-2">Batch</th>
                <th className="px-4 py-2">Course</th>
                <th className="px-4 py-2">Start</th>
                <th className="px-4 py-2">End</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">Open</th>
              </tr>
            </thead>
            <tbody>
              {enrollments.map((e) => (
                <tr
                  key={e.batchId}
                  className="border-t border-[color:var(--color-border)] hover:bg-[color:var(--color-surface)]"
                >
                  <td className="px-4 py-2 align-middle font-medium text-[color:var(--color-text)]">
                    {e.batchName}
                  </td>
                  <td className="px-4 py-2 align-middle text-[color:var(--color-text-muted)]">
                    {e.courseName}
                  </td>
                  <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)]">
                    {formatDate(e.startDate)}
                  </td>
                  <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)]">
                    {e.endDate ? formatDate(e.endDate) : '—'}
                  </td>
                  <td className="px-4 py-2 align-middle">
                    <EnrollmentStatusChip status={e.status} />
                  </td>
                  <td className="px-4 py-2 align-middle text-right">
                    <Link
                      to="/academics/batches/$batchId"
                      params={{ batchId: e.batchId }}
                      className="text-xs font-medium text-gold-700 hover:underline"
                    >
                      Open batch
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </RelationPanel>

      {canManage ? (
        <EnrollInBatchModal
          open={open}
          onClose={() => setOpen(false)}
          studentId={student.id}
          onSuccess={onChanged}
        />
      ) : null}
    </>
  );
}

function EnrollmentStatusChip({
  status,
}: {
  status: StudentEnrollmentRow['status'];
}): JSX.Element {
  const tone =
    status === 'active'
      ? 'bg-[color:var(--color-success)]/15 text-[color:var(--color-success)]'
      : status === 'paused'
        ? 'bg-gold-50 text-gold-700 dark:bg-gold-700/20'
        : status === 'completed'
          ? 'bg-[color:var(--color-surface)] text-[color:var(--color-text)]'
          : 'bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)]';
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize',
        tone,
      )}
    >
      {status}
    </span>
  );
}

interface EnrollInBatchModalProps {
  open: boolean;
  onClose: () => void;
  studentId: string;
  onSuccess: () => void;
}

function EnrollInBatchModal({
  open,
  onClose,
  studentId,
  onSuccess,
}: EnrollInBatchModalProps): JSX.Element {
  const today = new Date().toISOString().slice(0, 10);
  const [picked, setPicked] = useState<PickedEntity | null>(null);
  const [startDate, setStartDate] = useState(today);
  const { toast } = useToast();

  const submit = async (): Promise<{ ok: true }> => {
    if (!picked?.id) {
      throw new ApiError(400, 'invalid', 'Select a batch to enroll into.');
    }
    return apiRequest<{ ok: true }>(
      `/api/batches/${encodeURIComponent(picked.id)}/enrollments`,
      { method: 'POST', body: { studentId, startDate } },
    );
  };

  return (
    <AddRelationModal<{ ok: true }>
      open={open}
      onClose={() => {
        setPicked(null);
        setStartDate(today);
        onClose();
      }}
      title="Enroll in batch"
      description="Pick an active batch and set the student's start date."
      picker={
        <EntityPicker
          entity="batch"
          {...(picked ? { value: picked } : {})}
          onChange={(v) => {
            if (Array.isArray(v)) setPicked(v[0] ?? null);
            else setPicked(v.id ? v : null);
          }}
          filter={{ status: 'active' }}
        />
      }
      fields={
        <TextField
          label="Start date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
      }
      submitLabel="Enroll"
      canSubmit={Boolean(picked?.id) && Boolean(startDate)}
      onSubmit={submit}
      onSuccess={() => {
        toast('Student enrolled.', { variant: 'success' });
        onSuccess();
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Household tab
// ---------------------------------------------------------------------------

interface HouseholdTabProps {
  student: StudentDetail;
  canManage: boolean;
  onChanged: () => void;
}

function HouseholdTab({ student, canManage, onChanged }: HouseholdTabProps): JSX.Element {
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const [open, setOpen] = useState(false);
  const [unlinkPending, setUnlinkPending] = useState(false);

  const handleUnlink = async (): Promise<void> => {
    const household = student.household;
    if (!household) return;
    const ok = await confirm({
      title: 'Unlink family?',
      description: `${student.name} will no longer be linked to ${household.primaryContactName}.`,
      confirmLabel: 'Unlink',
      variant: 'danger',
    });
    if (!ok) return;
    setUnlinkPending(true);
    try {
      await apiRequest<{ ok: true }>(
        `/api/households/${encodeURIComponent(household.id)}/students/${encodeURIComponent(student.id)}`,
        { method: 'DELETE' },
      );
      toast('Family unlinked.', { variant: 'success' });
      onChanged();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not unlink family.', {
        variant: 'error',
      });
    } finally {
      setUnlinkPending(false);
    }
  };

  const household = student.household;
  const siblings = student.siblings ?? [];

  if (!household) {
    return (
      <>
        <RelationPanel
          title="Family"
          description="Parent contacts linked to this student."
          isEmpty
          empty={{
            copy: 'Not linked to a family.',
            cta: canManage ? (
              <Button
                size="sm"
                icon={<LinkIcon size={14} aria-hidden="true" />}
                onClick={() => setOpen(true)}
              >
                Link to family
              </Button>
            ) : undefined,
          }}
        >
          <span />
        </RelationPanel>

        {canManage ? (
          <LinkHouseholdModal
            open={open}
            onClose={() => setOpen(false)}
            studentId={student.id}
            onSuccess={onChanged}
          />
        ) : null}
      </>
    );
  }

  return (
    <section className="space-y-4">
      <RelationPanel
        title="Family"
        description={`Primary contact: ${household.primaryContactName}`}
        action={
          canManage ? (
            <Button
              size="sm"
              variant="ghost"
              loading={unlinkPending}
              onClick={() => {
                void handleUnlink();
              }}
            >
              Unlink
            </Button>
          ) : null
        }
      >
        <div className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[color:var(--color-text)]">
                {household.primaryContactName}
              </p>
              <p className="mt-0.5 text-xs text-[color:var(--color-text-muted)]">
                {household.primaryPhone}
                {household.primaryEmail ? ` · ${household.primaryEmail}` : ''}
              </p>
              {household.relation ? (
                <p className="mt-0.5 text-xs text-[color:var(--color-text-muted)]">
                  Relation: <span className="capitalize">{household.relation}</span>
                  {household.isPrimary ? ' · Primary' : ''}
                </p>
              ) : null}
            </div>
            <Link
              to="/people/households/$householdId"
              params={{ householdId: household.id }}
              className="text-xs font-medium text-gold-700 hover:underline"
            >
              Open family
            </Link>
          </div>
        </div>
      </RelationPanel>

      <RelationPanel
        title="Siblings"
        count={siblings.length}
        description="Other students linked to the same family."
        isEmpty={siblings.length === 0}
        empty={{ copy: 'No siblings linked to this family yet.' }}
      >
        <ul className="divide-y divide-[color:var(--color-border)] rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)]">
          {siblings.map((s) => (
            <li
              key={s.id}
              className="flex items-center justify-between gap-3 px-4 py-2.5"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-[color:var(--color-text)]">
                  {s.name}
                </p>
                <p className="truncate font-mono text-xs text-[color:var(--color-text-muted)]">
                  {s.admissionNumber}
                </p>
              </div>
              <Link
                to="/people/students/$studentId"
                params={{ studentId: s.id }}
                className="text-xs font-medium text-gold-700 hover:underline"
              >
                Open
              </Link>
            </li>
          ))}
        </ul>
      </RelationPanel>
    </section>
  );
}

interface LinkHouseholdModalProps {
  open: boolean;
  onClose: () => void;
  studentId: string;
  onSuccess: () => void;
}

function LinkHouseholdModal({
  open,
  onClose,
  studentId,
  onSuccess,
}: LinkHouseholdModalProps): JSX.Element {
  const [picked, setPicked] = useState<PickedEntity | null>(null);
  const { toast } = useToast();

  const submit = async (): Promise<{ ok: true }> => {
    if (!picked?.id) {
      throw new ApiError(400, 'invalid', 'Select a family to link.');
    }
    return apiRequest<{ ok: true }>(
      `/api/households/${encodeURIComponent(picked.id)}/students`,
      { method: 'POST', body: { studentIds: [studentId] } },
    );
  };

  return (
    <AddRelationModal<{ ok: true }>
      open={open}
      onClose={() => {
        setPicked(null);
        onClose();
      }}
      title="Link to family"
      description="Pick the family this student belongs to."
      picker={
        <EntityPicker
          entity="household"
          {...(picked ? { value: picked } : {})}
          onChange={(v) => {
            if (Array.isArray(v)) setPicked(v[0] ?? null);
            else setPicked(v.id ? v : null);
          }}
        />
      }
      submitLabel="Link"
      canSubmit={Boolean(picked?.id)}
      onSubmit={submit}
      onSuccess={() => {
        toast('Family linked.', { variant: 'success' });
        onSuccess();
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Portal access tab
// ---------------------------------------------------------------------------

function PortalTab({
  student,
  onChanged,
}: {
  student: StudentDetail;
  onChanged: () => void;
}): JSX.Element {
  return (
    <section className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
      <PortalAccessCard
        profileType="student"
        profileId={student.id}
        profileName={student.name}
        profileEmail={student.email}
        userId={student.userId}
        onChange={onChanged}
      />
    </section>
  );
}
